import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import reportWebVitals from "./reportWebVitals";
import { ToastContainer } from "react-toastify";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StoreProvider } from "easy-peasy";
import "./index.css";
import App from "./App";
import store from "./store";
import packageConfig from "../package.json";

// react-toastify styles
import "react-toastify/dist/ReactToastify.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000 * 3, // 3 minutes
      cacheTime: 60 * 1000 * 10, // 10 minutes
    },
  },
});

declare module "@mui/material/styles" {
  interface TypographyVariants {
    smReg: React.CSSProperties;
    smMed: React.CSSProperties;
    mdReg: React.CSSProperties;
    mdMed: React.CSSProperties;
    lgReg: React.CSSProperties;
    lgMed: React.CSSProperties;
    headingSm: React.CSSProperties;
    headingMd: React.CSSProperties;
    headingLg: React.CSSProperties;
    headingXl: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    smReg?: React.CSSProperties;
    smMed?: React.CSSProperties;
    mdReg?: React.CSSProperties;
    mdMed?: React.CSSProperties;
    lgReg?: React.CSSProperties;
    lgMed?: React.CSSProperties;
    headingSm?: React.CSSProperties;
    headingMd?: React.CSSProperties;
    headingLg?: React.CSSProperties;
    headingXl?: React.CSSProperties;
  }

  interface BreakpointOptions {
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
    laptop: true;
    desktop: true;
  }

  interface BreakpointOverrides {
    xs: true; // removes the `xs` breakpoint
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
    laptop: true;
    desktop: true;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body1: false;
    body2: false;
    h1: false;
    h2: false;
    h3: false;
    h4: false;
    h5: false;
    h6: false;
    caption: false;
    button: false;
    overline: false;
    smReg: true;
    smMed: true;
    mdReg: true;
    mdMed: true;
    lgReg: true;
    lgMed: true;
    headingSm: true;
    headingMd: true;
    headingLg: true;
    headingXl: true;
  }
}

const theme = createTheme({
  typography: {
    fontFamily: ["Inter", "sans-serif"].join(","),
    smReg: {
      fontSize: 12,
      fontWeight: 400,
    },
    smMed: {
      fontSize: 12,
      fontWeight: 500,
    },
    mdReg: {
      fontSize: 14,
      fontWeight: 400,
    },
    mdMed: {
      fontSize: 14,
      fontWeight: 500,
    },
    lgReg: {
      fontSize: 16,
      fontWeight: 400,
    },
    lgMed: {
      fontSize: 16,
      fontWeight: 500,
    },
    headingSm: {
      fontSize: 20,
      fontWeight: 500,
    },
    headingMd: {
      fontSize: 28,
      fontWeight: 500,
    },
    headingLg: {
      fontSize: 36,
      fontWeight: 600,
    },
    headingXl: {
      fontSize: 48,
      fontWeight: 700,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          smReg: "p",
          smMed: "p",
          mdReg: "p",
          mdMed: "p",
          lgReg: "p",
          lgMed: "p",
          headingSm: "h4",
          headingMd: "h3",
          headingLg: "h2",
          headingXl: "h1",
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      mobile: 0,
      tablet: 640,
      laptop: 1024,
      desktop: 1200,
    },
  },
});

const dsn = process.env.REACT_APP_SENTRY_DSN;
if (process.env.NODE_ENV !== "development" && dsn) {
  Sentry.init({
    dsn,
    integrations: [new BrowserTracing()],
    environment: process.env.NODE_ENV,
    release: packageConfig.version,
    tracesSampleRate: 1.0,
  });
}

//@ts-ignore
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <StoreProvider store={store}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <App />
            <ToastContainer />
          </ThemeProvider>
        </StoreProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
